// Orders table: Time / Side / Price / Qty / Level / Status. Hide filled/cancelled toggle.
// BE orders show taker (purple) vs maker (blue) color matching the levels chart.
import { useState } from "react";
import type { Order, Trade } from "@/types/api";
import { fmt, timeAgo } from "@/lib/format";

interface GridOrdersTableProps {
  orders: Order[];
  symbol: string;
  unmatchedOpenings?: Trade[];
  maxHoldMinutes?: number | null | undefined;
}

const STATUS_CLASS: Record<string, string> = {
  open: "text-success",
  pending: "text-warn",
  filled: "text-dim",
  cancelled: "text-muted",
};

// BE maker = opening held >= 2× max_hold_minutes (tightest, blue).
// BE taker = opening held >= 1× but < 2× (farther from entry, purple).
function isBeRelaxed(
  order: Order,
  unmatchedOpenings: Trade[],
  maxHoldMinutes: number | null | undefined,
): boolean {
  if (!maxHoldMinutes || !order.opening_trade_id) return false;
  const opener = unmatchedOpenings.find((u) => u.id === order.opening_trade_id);
  if (!opener) return false;
  return Date.now() - new Date(opener.traded_at).getTime() >= maxHoldMinutes * 60_000 * 2;
}

export default function GridOrdersTable({ orders, symbol, unmatchedOpenings = [], maxHoldMinutes }: GridOrdersTableProps) {
  const [hideFilled, setHideFilled] = useState(true);

  const visible = hideFilled
    ? orders.filter((o) => o.status === "open" || o.status === "pending")
    : orders;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted">Orders</h3>
        <button
          className={`inline-flex items-center h-7 px-3 text-xs font-medium rounded border transition-colors ${
            hideFilled
              ? "border-accent/60 text-accent bg-accent/10"
              : "border-border text-muted hover:text-text"
          }`}
          onClick={() => setHideFilled((v) => !v)}
        >
          Hide filled / cancelled
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="text-muted text-xs text-center py-4">No active orders</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-muted border-b border-border">
                <th className="text-left py-1 pr-3 font-normal">Time</th>
                <th className="text-left py-1 pr-3 font-normal">Side</th>
                <th className="text-right py-1 pr-3 font-normal">Price</th>
                <th className="text-right py-1 pr-3 font-normal">Qty</th>
                <th className="text-right py-1 pr-3 font-normal">Level</th>
                <th className="text-left py-1 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => (
                <tr key={o.id} className="border-b border-border/40 hover:bg-surface/50">
                  <td className="py-1 pr-3 text-dim">{o.created_at ? timeAgo(o.created_at) : "—"}</td>
                  <td className="py-1 pr-3">
                    <span className={o.side === "buy" ? "text-success" : "text-danger"}>
                      {o.side === "buy" ? "↑" : "↓"} {o.side.toUpperCase()}
                    </span>
                  </td>
                  <td className={`py-1 pr-3 text-right ${
                    o.close_reason === "break-even"
                      ? (isBeRelaxed(o, unmatchedOpenings, maxHoldMinutes) ? "text-info" : "text-accent")
                    : o.close_reason === "force" ? "text-info"
                    : "text-text"
                  }`}>{fmt(o.price, symbol)}</td>
                  <td className="py-1 pr-3 text-right text-dim">{o.quantity}</td>
                  <td className="py-1 pr-3 text-right text-dim">{o.grid_level}</td>
                  <td className={`py-1 ${STATUS_CLASS[o.status] ?? "text-muted"}`}>
                    {o.status}
                    {o.close_reason === "break-even" && (
                      isBeRelaxed(o, unmatchedOpenings, maxHoldMinutes)
                        ? <span className="ml-1 text-info">BE-M</span>
                        : <span className="ml-1 text-accent">BE-T</span>
                    )}
                    {o.close_reason === "force" && <span className="ml-1 text-info">FE</span>}
                    {o.close_reason === "grid" && o.opening_trade_id != null && <span className="ml-1 text-muted">TP</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
